import { useEffect } from 'react'

type PageSeoProps = {
  title: string
  description: string
  canonical: string
  image?: string
  type?: 'website' | 'article'
  keywords?: string[]
  schema?: object | object[]
}

function upsertMeta(attribute: 'name' | 'property', key: string, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`)
  const created = !element

  if (!element) {
    element = document.createElement('meta')
    element.setAttribute(attribute, key)
    document.head.appendChild(element)
  }

  const previous = element.getAttribute('content')
  element.setAttribute('content', content)

  return () => {
    if (!element) {
      return
    }

    if (created) {
      element.remove()
      return
    }

    if (previous === null) {
      element.removeAttribute('content')
      return
    }

    element.setAttribute('content', previous)
  }
}

function upsertLink(rel: string, href: string) {
  let element = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`)
  const created = !element

  if (!element) {
    element = document.createElement('link')
    element.setAttribute('rel', rel)
    document.head.appendChild(element)
  }

  const previous = element.getAttribute('href')
  element.setAttribute('href', href)

  return () => {
    if (!element) {
      return
    }

    if (created) {
      element.remove()
      return
    }

    if (previous === null) {
      element.removeAttribute('href')
      return
    }

    element.setAttribute('href', previous)
  }
}

export default function PageSeo({
  title,
  description,
  canonical,
  image,
  type = 'website',
  keywords,
  schema,
}: PageSeoProps) {
  useEffect(() => {
    const previousTitle = document.title
    document.title = title

    const cleanups = [
      upsertMeta('name', 'description', description),
      upsertMeta('property', 'og:title', title),
      upsertMeta('property', 'og:description', description),
      upsertMeta('property', 'og:url', canonical),
      upsertMeta('property', 'og:type', type),
      upsertMeta('name', 'twitter:card', 'summary_large_image'),
      upsertMeta('name', 'twitter:title', title),
      upsertMeta('name', 'twitter:description', description),
      upsertLink('canonical', canonical),
    ]

    if (image) {
      cleanups.push(upsertMeta('property', 'og:image', image))
      cleanups.push(upsertMeta('name', 'twitter:image', image))
    }

    if (keywords?.length) {
      cleanups.push(upsertMeta('name', 'keywords', keywords.join(', ')))
    }

    // Remove any prerendered ld+json scripts (injected at build time, no data-page-seo attr)
    document.head
      .querySelectorAll<HTMLScriptElement>('script[type="application/ld+json"]:not([data-page-seo])')
      .forEach((el) => el.remove())

    const scripts: HTMLScriptElement[] = []
    const schemaItems = Array.isArray(schema) ? schema : schema ? [schema] : []

    schemaItems.forEach((item, index) => {
      const script = document.createElement('script')
      script.type = 'application/ld+json'
      script.dataset.pageSeo = `schema-${index}`
      script.text = JSON.stringify(item)
      document.head.appendChild(script)
      scripts.push(script)
    })

    return () => {
      document.title = previousTitle
      cleanups.reverse().forEach((cleanup) => cleanup())
      scripts.forEach((script) => script.remove())
    }
  }, [canonical, description, image, keywords, schema, title, type])

  return null
}
